import jwt_decode from "jwt-decode";
import React from "react";
import { useDispatch } from "react-redux";
import { Navigate, Outlet } from "react-router-dom";

import { FetchToken, setUser } from "../../export/user";
import { setWorker } from "../../export/web-worker";
import { applySupervisorLanguageOnce } from "./language";
import PermissionsGate from "./permissions-gate";

interface TokenData {
	exp: number;
	[key: string]: any;
}

interface PrivateRouteProps {
	authenticated: boolean;
	workerApp: boolean;
}

const PrivateRoute = ({ authenticated, workerApp }: PrivateRouteProps) => {
	const dispatch = useDispatch();

	const isTokenValid = (token: string) => {
		const { exp }: TokenData = jwt_decode(token);
		return Date.now() / 1000 < exp;
	};

	const handleWorkerAuth = () => {
		const workerToken = localStorage.getItem("worker-token");
		const workerRefreshToken = localStorage.getItem("worker-refreshToken");

		if (!workerToken || !workerRefreshToken) {
			return <Navigate to='/wl' replace />;
		}

		if (isTokenValid(workerToken)) {
			dispatch(setWorker());
			return <Outlet />;
		}

		return <Outlet />;
	};

	const handleUserAuth = () => {
		const token = localStorage.getItem("token");
		const refreshToken = localStorage.getItem("refreshToken");

		if (!token || !refreshToken) {
			return <Navigate to='/login' replace />;
		}

		if (isTokenValid(token)) {
			const decodedToken: TokenData = jwt_decode(token);
			dispatch(
        setUser({
          userRoles: decodedToken.userRoles,
          id: decodedToken.id,
          showHeatMap: decodedToken.showHeatMap,
          showJobExpiry: decodedToken.showJobExpiry,
          showCrossSkilling: decodedToken.showCrossSkilling,
          shiftCancelReasonEnabled: decodedToken.shiftCancelReasonEnabled,
          skillPriorityEnabled: decodedToken["skillPriority.Enabled"],
          useObsoleteSkills:
            decodedToken["skillPriority.workerScore.useObsoleteSkill"],
          customizedBrandingEnabled: decodedToken.customizedBrandingEnabled,
          certificationsEnabled: decodedToken.certificationsEnabled,
          skillExpiryEnabled: decodedToken.skillExpiryEnabled,
          skillInsightsEnabled: decodedToken.skillInsightsEnabled,
          jobsMatrixEnabled: decodedToken["jobsMatrix.enabled"],
          // Customer boards — claim name is unprefixed in some tokens.
          revenueForecastingEnabled:
            decodedToken["clientApp.revenueForecastingEnabled"] ??
            decodedToken.revenueForecastingEnabled,
          labBoardEnabled:
            decodedToken["clientApp.labBoardEnabled"] ??
            decodedToken.labBoardEnabled,
          supervisorDashboardEnabled: decodedToken.supervisorDashboardEnabled,
          minDurationHrs: decodedToken.minDurationHrs,
          maxDurationHrs: decodedToken.maxDurationHrs,
          defaultDurationHrs: decodedToken.defaultDurationHrs,
          showWorkerProfile: decodedToken.showWorkerProfile,
          showShiftPurpose: decodedToken.showShiftPurpose,
          fullTimeSchedule: decodedToken.fullTimeSchedule,
          ftSchedulerEnabled: decodedToken["fullTimeScheduling.enabled"],
          ftSchedulerEnabledLocations:
            decodedToken["FullTimeSchedulingEnabledLocations"],
          FTScheduleGenerationEnabledLocations:
            decodedToken["FTScheduleGenerationEnabledLocations"],
          showShiftSwap: decodedToken.showShiftSwap,
          restrictFlexHours: decodedToken.restrictFlexHours,
          schedulerOffsetEnabled: decodedToken.schedulerOffsetEnabled,
          firstName: decodedToken.firstName,
          lastName: decodedToken.lastName,
          username: decodedToken.email,
          company: decodedToken.company,
          companyId: decodedToken.companyId,
          attendMgmt: decodedToken["attendMgmt.enabled"],
          leaveCalendarHeatmap: decodedToken["leaveCalendarHeatmap.enabled"],
          callOffLineEnabledLocations:
            decodedToken["CallOffLineEnabledLocations"] || [],
          attendanceManagementEnabledLocations:
            decodedToken["AttendanceMgmtEnabledLocations"],
          shiftBySkillsMinSkillLevel: decodedToken.shiftBySkillsMinSkillLevel,
          shiftBySkillsEnabled: decodedToken.shiftBySkillsEnabled,
          assignShiftToWorkerEnabled: decodedToken.assignShiftToWorkerEnabled,
          weekStartDay: decodedToken.weekStartDay,
          // weekStartTime: decodedToken.weekStartTime, //TODO: uncomment when supporting offset stories are integrated
          jobSkillLevelMapping: decodedToken["jobSkill.skillLevelEnabled"],
          workerSubTypeEnabled: decodedToken["workerSubType.enabled"],
          skillLevelRequirementsEnabled:
            decodedToken.skillLevelRequirementsEnabled,
          allowPartialSignups: decodedToken["partialShiftSignup.enabled"],
          badgeNumberEnabled: decodedToken["badgeNumber.Enabled"],
          badgeNumberEnabledLocations:
            decodedToken["BadgeNumberEnabledLocations"],
          maxBroadcastMessageLength: decodedToken.maxBroadcastMessageLength,
          skillsMatrixExportEnabled:
            decodedToken["skillMatrix.excelExport.enabled"],
          EnableSkillEvaluationForm: decodedToken.EnableSkillEvaluationForm,
          leaveBalanceUpdateEnabled: decodedToken["leaveBalanceMgmt.enabled"],
          vakaySSOLinkEnabled: decodedToken["EnableVakaySSOLink"],
          punchImportEnabled: decodedToken["punchImport.enabled"],
          shiftWorkerTypeFilterEnabled: decodedToken["shift.workerTypeFilter.enabled"],
          urgentShiftEnabledLocations: decodedToken["UrgentShiftEnabledLocations"] || [],
          minWeekHoursNotifEnabledLocations: Array.isArray(decodedToken["MinWeekHoursNotifEnabledLocations"]) ? decodedToken["MinWeekHoursNotifEnabledLocations"] : [],
          supervisorMultilingualEnabled: decodedToken["supervisorMultilingualEnabled"] === true,
          languagePreference: decodedToken["languagePreference"],
        })
      );
			applySupervisorLanguageOnce(
				decodedToken["supervisorMultilingualEnabled"] === true,
				decodedToken["languagePreference"]
			);
			return <PermissionsGate />;
		}

		return <PermissionsGate />;
	};

	if (authenticated) {
		return workerApp ? <Outlet /> : <PermissionsGate />;
	}

	return workerApp ? handleWorkerAuth() : handleUserAuth();
};

export default PrivateRoute;
